import api from "./axios";
import { fetchOwnerProfile, fetchOwnerVehiclesWithBookings } from "./ownerCarsApi";

function withAuth() {
  const token = localStorage.getItem("ownerToken") || localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function unwrapAxiosError(error, fallbackMessage) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) {
    throw new Error(data.trim());
  }
  if (data?.message) {
    throw new Error(String(data.message));
  }
  if (data?.error) {
    throw new Error(String(data.error));
  }
  if (error?.message) {
    throw new Error(String(error.message));
  }
  throw new Error(fallbackMessage);
}

function clampPage(v, fallback = 0) {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.trunc(n);
}

function clampSize(v, fallback = 20) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.min(100, Math.trunc(n));
}

function cleanText(value) {
  const s = String(value || "").trim().replace(/\s+/g, " ");
  return s || "";
}

function cleanIfsc(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "");
}

function cleanAccountNumber(value) {
  return String(value || "").replace(/\D+/g, "");
}

function cleanUpi(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

/**
 * UI payload can come from multiple forms:
 * - EarningsDashboard uses ifscCode / bankAccountNumber
 * - Other page may use ifsc / accountNumber
 *
 * Convert everything to backend DTO shape:
 * { method, accountHolderName, bankName, ifsc, accountNumber, upiId }
 */
function normalizePayoutPayload(payload = {}) {
  const method = String(payload.method || "UPI").trim().toUpperCase() === "BANK" ? "BANK" : "UPI";

  if (method === "UPI") {
    return {
      method: "UPI",
      upiId: cleanUpi(payload.upiId),
    };
  }

  const accountNumber = cleanAccountNumber(
    payload.accountNumber ?? payload.bankAccountNumber ?? ""
  );

  return {
    method: "BANK",
    accountHolderName: cleanText(payload.accountHolderName),
    bankName: cleanText(payload.bankName),
    ifsc: cleanIfsc(payload.ifsc ?? payload.ifscCode),
    ...(accountNumber ? { accountNumber } : {}),
  };
}

export async function fetchOwnerPayoutAccount() {
  try {
    const { data } = await api.get("/owners/payout/account", withAuth());
    return data ?? null;
  } catch (error) {
    unwrapAxiosError(error, "Failed to load payout account.");
  }
}

export async function saveOwnerPayoutAccount(payload) {
  try {
    const body = normalizePayoutPayload(payload);
    const { data } = await api.put("/owners/payout/account", body, withAuth());
    return data ?? null;
  } catch (error) {
    unwrapAxiosError(error, "Failed to save payout account.");
  }
}

export async function submitOwnerPayoutAccount(payload) {
  try {
    const body = normalizePayoutPayload(payload);
    const { data } = await api.post("/owners/payout/account/submit", body, withAuth());
    return data ?? null;
  } catch (error) {
    unwrapAxiosError(error, "Failed to submit payout account.");
  }
}

export async function fetchOwnerPayoutSummary() {
  try {
    const { data } = await api.get("/owners/payout/summary", withAuth());
    return data ?? null;
  } catch (error) {
    unwrapAxiosError(error, "Failed to load payout summary.");
  }
}

export async function fetchOwnerPayoutTransactions(params = {}) {
  try {
    const page = clampPage(params.page, 0);
    const size = clampSize(params.size, 20);
    const { data } = await api.get("/owners/payout/transactions", {
      ...withAuth(),
      params: { page, size },
    });
    return data ?? { content: [], number: page, size, totalElements: 0, totalPages: 0 };
  } catch (error) {
    unwrapAxiosError(error, "Failed to load payout transactions.");
  }
}

export async function fetchOwnerPayoutScreenData({ page = 0, size = 20 } = {}) {
  const owner = await fetchOwnerProfile();
  if (!owner?.id) {
    throw new Error("Unable to resolve owner id from profile.");
  }

  const [vehicles, account, summary, txnPage] = await Promise.all([
    fetchOwnerVehiclesWithBookings(owner.id),
    fetchOwnerPayoutAccount(),
    fetchOwnerPayoutSummary(),
    fetchOwnerPayoutTransactions({ page, size }),
  ]);

  return {
    owner,
    vehicles: Array.isArray(vehicles) ? vehicles : [],
    account: account ?? null,
    summary: summary ?? null,
    txnPage: txnPage ?? { content: [], number: page, size, totalElements: 0, totalPages: 0 },
  };
}